import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';

import { useTriggerPipeline } from '../../hooks/use-experiment-pipeline';
import { PipelineStartStage } from '../../services/experiment-pipeline.service';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  experimentId: string;
  /**
   * `full` re-runs curation + enrichment + approval + delivery (expensive — LLM calls).
   * `delivery-only` skips straight to the GMC push using the already-enriched products.
   */
  mode: 'full' | 'delivery-only';
}

/**
 * Confirmation modal for `POST /experiments/{id}/pipeline/trigger`. Two
 * variants — full pipeline (which incurs LLM cost across curation +
 * enrichment) and delivery-only retry (no LLM, just the GMC re-push). The
 * cost copy is the load-bearing UX detail: operators reach for "Retry"
 * after a failure and an undifferentiated button hides the bill.
 */
export function RetryPipelineDialog({ open, onOpenChange, experimentId, mode }: Props) {
  const { t } = useTranslation('experiments');
  const trigger = useTriggerPipeline();

  const handleConfirm = () => {
    trigger.mutate(
      {
        experimentId,
        startFromStage:
          mode === 'delivery-only' ? PipelineStartStage.DELIVERY : PipelineStartStage.CURATION,
      },
      {
        onSuccess: () => {
          onOpenChange(false);
        },
      },
    );
  };

  const titleKey =
    mode === 'delivery-only' ? 'detail.retry.delivery.title' : 'detail.retry.full.title';
  const descriptionKey =
    mode === 'delivery-only'
      ? 'detail.retry.delivery.description'
      : 'detail.retry.full.description';
  const confirmKey =
    mode === 'delivery-only' ? 'detail.retry.delivery.confirm' : 'detail.retry.full.confirm';

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t(titleKey)}</AlertDialogTitle>
          <AlertDialogDescription className="space-y-3">
            <span className="block">{t(descriptionKey)}</span>
            <span className="text-foreground bg-muted block rounded-md p-3 text-sm">
              {t(
                mode === 'delivery-only'
                  ? 'detail.retry.delivery.costNote'
                  : 'detail.retry.full.costNote',
              )}
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={trigger.isPending} className="cursor-pointer">
            {t('detail.retry.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={trigger.isPending}
            className="cursor-pointer"
          >
            {trigger.isPending && (
              <Loader2 aria-hidden="true" className="mr-1.5 h-4 w-4 animate-spin" />
            )}
            {t(confirmKey)}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
